import { useCallback, useEffect, useId, useState } from "react";
import {
  assignPhoneNumber,
  cancelNumberRequest,
  claimPhoneNumber,
  createNumberRequest,
  createOptOut,
  deleteOptOut,
  getMessagingNumberQuota,
  getMessagingPhoneNumbers,
  getNumberRequests,
  getOptOuts,
  releaseMessagingPhoneNumber,
  revokePhoneNumber,
  searchAvailableNumbers,
} from "../../services/messaging";
import { getOrganizationMembers } from "../../services/calendar";
import { ApiError } from "../../services/api";
import { ConfirmModal } from "../../components/ConfirmModal";
import type { OrganizationMember } from "../../types/api";
import type {
  AvailableNumber,
  MessagingNumberQuota,
  MessagingNumberRequest,
  MessagingOptOut,
  MessagingPhoneNumber,
} from "../../types/messaging";
import "./PhoneNumbersPage.css";

/** Twilio prices in USD; a null cost means the lookup was unavailable, not that it is free. */
const formatCost = (cost: number | null): string =>
  cost === null ? "price unavailable" : `$${cost.toFixed(2)}/mo`;

/** "+15135550100" reads as a phone number rather than a serial number. */
const formatE164 = (e164: string): string => {
  const digits = e164.replace(/\D/g, "");
  if (digits.length === 11 && digits.startsWith("1")) {
    return `(${digits.slice(1, 4)}) ${digits.slice(4, 7)}-${digits.slice(7)}`;
  }
  return e164;
};

const REQUEST_STATUS_LABEL: Record<MessagingNumberRequest["status"], string> = {
  Pending: "Waiting on RosterPilot",
  Approved: "Approved",
  Denied: "Not approved",
  Fulfilled: "Number added",
  Cancelled: "Withdrawn",
};

/**
 * Admin surface for the organization's leased messaging numbers, the search that acquires
 * them, and the opt-out register.
 *
 * Three things here are policy rather than UI, and the copy carries them deliberately: an
 * org-assigned number has no privacy expectation (whoever inherits it reads everything),
 * an opt-out binds every number in the organization rather than the one that received the
 * STOP, and releasing a number is permanent because it goes back to Twilio's pool.
 */
export const PhoneNumbersPage = () => {
  const [numbers, setNumbers] = useState<MessagingPhoneNumber[]>([]);
  const [optOuts, setOptOuts] = useState<MessagingOptOut[]>([]);
  const [requests, setRequests] = useState<MessagingNumberRequest[]>([]);
  const [quota, setQuota] = useState<MessagingNumberQuota | null>(null);
  const [members, setMembers] = useState<OrganizationMember[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  const [areaCode, setAreaCode] = useState("");
  const [contains, setContains] = useState("");
  const [results, setResults] = useState<AvailableNumber[] | null>(null);
  const [searching, setSearching] = useState(false);
  const [claiming, setClaiming] = useState<string | null>(null);

  const [requestReason, setRequestReason] = useState("");
  const [newOptOutPhone, setNewOptOutPhone] = useState("");
  const [pendingRelease, setPendingRelease] = useState<MessagingPhoneNumber | null>(null);
  const [releasing, setReleasing] = useState(false);

  // Ids for the group labels: a <label> naming a set of controls is wrong for screen
  // readers, so each set gets a <span> plus role="group" aria-labelledby.
  const searchGroupLabelId = useId();
  const resultsGroupLabelId = useId();
  const areaCodeId = useId();
  const containsId = useId();

  const atQuota = quota !== null && quota.remaining <= 0;
  const pendingRequest = requests.find((r) => r.status === "Pending") ?? null;

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [loadedNumbers, loadedOptOuts, loadedQuota, loadedRequests] =
        await Promise.all([
          getMessagingPhoneNumbers(),
          getOptOuts(),
          getMessagingNumberQuota(),
          getNumberRequests(),
        ]);
      setNumbers(loadedNumbers);
      setOptOuts(loadedOptOuts);
      setQuota(loadedQuota);
      setRequests(loadedRequests);
      setError(null);
    } catch {
      setError("Could not load your messaging numbers. Try reloading the page.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  useEffect(() => {
    // Best effort: without the member list the assign dropdown is empty, but everything
    // else on the page still works.
    getOrganizationMembers()
      .then(setMembers)
      .catch(() => setMembers([]));
  }, []);

  const runAction = async (action: () => Promise<unknown>, success?: string) => {
    setError(null);
    setNotice(null);
    try {
      await action();
      if (success) setNotice(success);
      await load();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Something went wrong.");
    }
  };

  const handleSearch = async () => {
    setError(null);
    setNotice(null);
    setSearching(true);
    try {
      const search = await searchAvailableNumbers({
        areaCode: areaCode.trim() || undefined,
        contains: contains.trim() || undefined,
      });
      setResults(search.numbers);
      setQuota({
        used: search.quotaUsed,
        limit: search.quotaLimit,
        remaining: Math.max(0, search.quotaLimit - search.quotaUsed),
      });
    } catch (err) {
      setResults(null);
      if (err instanceof ApiError && err.statusCode === 503) {
        setError(
          "Number search is not switched on yet. Ask an admin to finish the Twilio setup."
        );
      } else {
        setError(err instanceof Error ? err.message : "Could not search for numbers.");
      }
    } finally {
      setSearching(false);
    }
  };

  const handleClaim = async (candidate: AvailableNumber) => {
    setError(null);
    setNotice(null);
    setClaiming(candidate.phoneNumber);
    try {
      await claimPhoneNumber({ phoneNumber: candidate.phoneNumber });
      setNotice(
        `${formatE164(candidate.phoneNumber)} is yours. Assign it to a coach to start texting.`
      );
      // Drop it from the results so it can't be claimed twice from a stale list.
      setResults((current) =>
        current?.filter((n) => n.phoneNumber !== candidate.phoneNumber) ?? null
      );
      await load();
    } catch (err) {
      const code =
        err instanceof ApiError && err.response !== null && typeof err.response === "object"
          ? (err.response as { code?: string }).code
          : undefined;

      if (code === "number_unavailable") {
        // Expected: Twilio's inventory moves between a search and a claim.
        setError(
          `${formatE164(candidate.phoneNumber)} was taken by someone else. Search again for a fresh list.`
        );
        setResults((current) =>
          current?.filter((n) => n.phoneNumber !== candidate.phoneNumber) ?? null
        );
      } else if (code === "quota_exceeded") {
        setError(
          "You are using all of your numbers. Request another one below and we will review it."
        );
        await load();
      } else {
        setError(err instanceof Error ? err.message : "Could not set up that number.");
      }
    } finally {
      setClaiming(null);
    }
  };

  const handleRequest = (requestedE164?: string) =>
    runAction(async () => {
      await createNumberRequest({
        areaCode: areaCode.trim() || null,
        containsPattern: contains.trim() || null,
        requestedE164: requestedE164 ?? null,
        reason: requestReason.trim() || null,
      });
      setRequestReason("");
    }, "Request sent. We will get back to you.");

  const confirmRelease = async () => {
    if (!pendingRelease) return;
    setReleasing(true);
    const label = formatE164(pendingRelease.phoneNumber);
    try {
      await releaseMessagingPhoneNumber(pendingRelease.id);
      setPendingRelease(null);
      setNotice(`${label} has been given up. Your conversation history is still here.`);
      setError(null);
      await load();
    } catch (err) {
      setPendingRelease(null);
      setError(
        err instanceof ApiError && err.statusCode === 502
          ? `We could not give ${label} back to Twilio, so nothing was changed. Try again in a few minutes.`
          : err instanceof Error
            ? err.message
            : "Could not release that number."
      );
    } finally {
      setReleasing(false);
    }
  };

  return (
    <div className="phone-numbers-page">
      <div className="phone-numbers-page__header">
        <h2>Messaging numbers</h2>
        {quota && (
          <p className="phone-numbers-page__quota">
            Using {quota.used} of {quota.limit}{" "}
            {quota.limit === 1 ? "number" : "numbers"}
          </p>
        )}
      </div>

      {/* Both banners appear after an action, so a screen reader needs to be told. */}
      <div role="status" aria-live="polite" className="phone-numbers-page__announcer">
        {error && <div className="phone-numbers-page__error">{error}</div>}
        {notice && <div className="phone-numbers-page__notice">{notice}</div>}
      </div>

      <section className="phone-numbers-page__section">
        <h3>Your numbers</h3>
        <p className="phone-numbers-page__hint">
          Numbers belong to the organization, not to the coach holding them. A number kept
          through a staff change carries its full conversation history to the next holder, so
          an organization number carries no privacy expectation. Conversations that should
          not be staff-visible belong on a personal phone.
        </p>

        {loading ? (
          <div className="phone-numbers-page__loading">Loading your numbers...</div>
        ) : numbers.length === 0 ? (
          <div className="phone-numbers-page__empty">
            You do not have a number yet. Search below to get one.
          </div>
        ) : (
          <ul className="phone-numbers-page__list">
            {numbers.map((number) => (
              <li key={number.id} className="phone-numbers-page__item">
                <div className="phone-numbers-page__item-head">
                  <span className="phone-numbers-page__number">
                    {formatE164(number.phoneNumber)}
                  </span>
                  {number.friendlyName && (
                    <span className="phone-numbers-page__label">
                      {number.friendlyName}
                    </span>
                  )}
                  {number.provisioningSource === "Leased" && (
                    <span className="phone-numbers-page__cost">
                      {formatCost(number.monthlyCostUsd)}
                    </span>
                  )}
                  <button
                    type="button"
                    className="phone-numbers-page__release"
                    onClick={() => setPendingRelease(number)}
                    aria-label={`Give up ${formatE164(number.phoneNumber)}`}
                  >
                    Give up
                  </button>
                </div>

                <div
                  className="phone-numbers-page__holders"
                  role="group"
                  aria-label={`Coaches holding ${formatE164(number.phoneNumber)}`}
                >
                  {number.holders.length === 0 ? (
                    <span className="phone-numbers-page__unassigned">
                      Not assigned yet. Replies still arrive in the shared inbox.
                    </span>
                  ) : (
                    number.holders.map((holder) => (
                      <span
                        key={holder.userId}
                        className="phone-numbers-page__holder"
                      >
                        {holder.userName ?? holder.userId}
                        <button
                          type="button"
                          onClick={() =>
                            runAction(
                              () => revokePhoneNumber(number.id, holder.userId),
                              "Coach removed. The organization keeps the number."
                            )
                          }
                          aria-label={`Remove ${holder.userName ?? "this coach"} from ${formatE164(number.phoneNumber)}`}
                        >
                          &times;
                        </button>
                      </span>
                    ))
                  )}
                </div>

                <select
                  value=""
                  aria-label={`Assign ${formatE164(number.phoneNumber)} to a coach`}
                  onChange={(e) => {
                    if (!e.target.value) return;
                    void runAction(
                      () => assignPhoneNumber(number.id, e.target.value),
                      "Number assigned."
                    );
                  }}
                >
                  <option value="">Assign to...</option>
                  {members
                    .filter(
                      (m) => !number.holders.some((h) => h.userId === m.id)
                    )
                    .map((member) => (
                      <option key={member.id} value={member.id}>
                        {member.displayName}
                      </option>
                    ))}
                </select>
              </li>
            ))}
          </ul>
        )}
      </section>

      <section className="phone-numbers-page__section">
        <h3>Get a number</h3>
        <p className="phone-numbers-page__hint">
          Pick an area code close to the players you are recruiting. A local number gets
          answered more often than one from out of state. Numbers are billed to your
          RosterPilot plan, and you can give one back at any time.
        </p>

        <span id={searchGroupLabelId} className="phone-numbers-page__group-label">
          Search for an available number
        </span>
        <div
          className="phone-numbers-page__inline-form"
          role="group"
          aria-labelledby={searchGroupLabelId}
        >
          <label htmlFor={areaCodeId} className="phone-numbers-page__field-label">
            Area code
          </label>
          <input
            id={areaCodeId}
            type="text"
            inputMode="numeric"
            maxLength={3}
            value={areaCode}
            onChange={(e) => setAreaCode(e.target.value.replace(/\D/g, ""))}
            placeholder="513"
          />
          <label htmlFor={containsId} className="phone-numbers-page__field-label">
            Contains
          </label>
          <input
            id={containsId}
            type="text"
            value={contains}
            onChange={(e) => setContains(e.target.value)}
            placeholder="Digits or letters, optional"
          />
          <button
            type="button"
            onClick={handleSearch}
            disabled={searching}
          >
            {searching ? "Searching..." : "Search"}
          </button>
        </div>

        {atQuota && (
          <p className="phone-numbers-page__warning">
            You are using all {quota?.limit} of your numbers. You can still pick one below
            and request it, and we will review the request.
          </p>
        )}

        {/* Results arrive asynchronously, so their count has to be announced. */}
        <div role="status" aria-live="polite" className="phone-numbers-page__announcer">
          {results !== null &&
            (results.length === 0
              ? "No numbers matched. Try a different area code."
              : `${results.length} ${results.length === 1 ? "number" : "numbers"} available.`)}
        </div>

        {results !== null && results.length > 0 && (
          <>
            <span id={resultsGroupLabelId} className="phone-numbers-page__group-label">
              Available numbers
            </span>
            <ul
              className="phone-numbers-page__results"
              role="group"
              aria-labelledby={resultsGroupLabelId}
            >
              {results.map((candidate) => (
                <li key={candidate.phoneNumber} className="phone-numbers-page__result">
                  <span className="phone-numbers-page__number">
                    {formatE164(candidate.phoneNumber)}
                  </span>
                  <span className="phone-numbers-page__locality">
                    {[candidate.locality, candidate.region]
                      .filter(Boolean)
                      .join(", ") || "United States"}
                  </span>
                  <span className="phone-numbers-page__cost">
                    {formatCost(candidate.monthlyCostUsd)}
                  </span>
                  {atQuota ? (
                    <button
                      type="button"
                      onClick={() => handleRequest(candidate.phoneNumber)}
                      disabled={pendingRequest !== null}
                      aria-label={`Request ${formatE164(candidate.phoneNumber)}`}
                    >
                      Request
                    </button>
                  ) : (
                    <button
                      type="button"
                      onClick={() => handleClaim(candidate)}
                      disabled={claiming !== null}
                      aria-label={`Get ${formatE164(candidate.phoneNumber)}`}
                    >
                      {claiming === candidate.phoneNumber ? "Setting up..." : "Get it"}
                    </button>
                  )}
                </li>
              ))}
            </ul>
          </>
        )}
      </section>

      <section className="phone-numbers-page__section">
        <h3>Requests</h3>
        <p className="phone-numbers-page__hint">
          Need more numbers than your plan includes? Tell us what you are after and we will
          sort it out. One request at a time.
        </p>

        {pendingRequest === null && (
          <div className="phone-numbers-page__inline-form">
            <label
              htmlFor="number-request-reason"
              className="phone-numbers-page__field-label"
            >
              What do you need it for?
            </label>
            <input
              id="number-request-reason"
              type="text"
              value={requestReason}
              onChange={(e) => setRequestReason(e.target.value)}
              placeholder="Second recruiting coordinator starting in September"
            />
            <button type="button" onClick={() => handleRequest()}>
              Send request
            </button>
          </div>
        )}

        {requests.length === 0 ? (
          <div className="phone-numbers-page__empty">No requests yet.</div>
        ) : (
          <ul className="phone-numbers-page__requests">
            {requests.map((request) => (
              <li key={request.id}>
                <span className="phone-numbers-page__request-status">
                  {REQUEST_STATUS_LABEL[request.status]}
                </span>
                <span>
                  {request.requestedE164
                    ? formatE164(request.requestedE164)
                    : request.areaCode
                      ? `Area code ${request.areaCode}`
                      : "No preference"}
                </span>
                {request.fulfilledPhoneNumber && (
                  <span className="phone-numbers-page__number">
                    {formatE164(request.fulfilledPhoneNumber)}
                  </span>
                )}
                {request.decisionNote && (
                  <span className="phone-numbers-page__request-note">
                    {request.decisionNote}
                  </span>
                )}
                {request.status === "Pending" && (
                  <button
                    type="button"
                    onClick={() =>
                      runAction(
                        () => cancelNumberRequest(request.id),
                        "Request withdrawn."
                      )
                    }
                    aria-label="Withdraw this number request"
                  >
                    Withdraw
                  </button>
                )}
              </li>
            ))}
          </ul>
        )}
      </section>

      <section className="phone-numbers-page__section">
        <h3>Opted out ({optOuts.length})</h3>
        <p className="phone-numbers-page__hint">
          An opt-out applies to every number in the organization, not just the one that
          received it. These recipients are left out of every send and shown as suppressed
          in the send preview.
        </p>

        <div className="phone-numbers-page__inline-form">
          <label htmlFor="new-opt-out" className="phone-numbers-page__field-label">
            Add an opt-out given by phone or email
          </label>
          <input
            id="new-opt-out"
            type="text"
            value={newOptOutPhone}
            onChange={(e) => setNewOptOutPhone(e.target.value)}
            placeholder="+15135550100"
          />
          <button
            type="button"
            onClick={() =>
              runAction(async () => {
                await createOptOut(newOptOutPhone.trim());
                setNewOptOutPhone("");
              }, "Opt-out recorded.")
            }
            disabled={!newOptOutPhone.trim()}
          >
            Add
          </button>
        </div>

        {optOuts.length > 0 && (
          <ul className="phone-numbers-page__optouts">
            {optOuts.map((optOut) => (
              <li key={optOut.id}>
                <span>{formatE164(optOut.normalizedPhone)}</span>
                <span className="phone-numbers-page__optout-source">
                  {optOut.source === "StopKeyword" ? "Replied STOP" : "Added by hand"}
                </span>
                <button
                  type="button"
                  onClick={() =>
                    runAction(
                      () => deleteOptOut(optOut.normalizedPhone),
                      "Opt-out lifted."
                    )
                  }
                  aria-label={`Lift the opt-out for ${formatE164(optOut.normalizedPhone)}`}
                >
                  Lift
                </button>
              </li>
            ))}
          </ul>
        )}
      </section>

      <ConfirmModal
        isOpen={pendingRelease !== null}
        onClose={() => setPendingRelease(null)}
        onConfirm={confirmRelease}
        busy={releasing}
        title="Give up this number?"
        message={
          pendingRelease
            ? `${formatE164(pendingRelease.phoneNumber)} goes back to Twilio and you cannot get it back. Someone else may end up with it, so a player who texts this number later could reach a stranger. Your conversation history stays in RosterPilot, and the monthly charge stops.`
            : ""
        }
        confirmText="Give it up"
        cancelText="Keep it"
      />
    </div>
  );
};
